import { API_BASE } from './api-base';
import { identifyFromInstance } from './sentry-identity';

const DUPLEXER_HOST = 'duplexer.wix.com';
const APP_DEF_ID = 'f5b4ce00-4f7b-4066-a607-0277d16780b8';

/**
 * `ended`: `individuals-group-chat` only — a group ENDED (VMR #34789). `receipt` / `typing` / `presence`: the same
 * channel (design §21, 2026-11-01) — a person's delivered / read position, "is typing…" (a Maestro "is thinking…"),
 * and online / last seen. Each is bound on the channel the first time a listener asks for it.
 */
export type DuplexerEventName = 'created' | 'updated' | 'deleted' | 'action' | 'ended' | 'receipt' | 'typing' | 'presence';

export interface DuplexerPayload<T> {
  entityId?: string;
  entity?: T | null;
}

type Listener = (payload: DuplexerPayload<unknown>) => void;

/**
 * ONE duplexer connection for the whole app, not one per subscription.
 *
 * Every channel used to open its OWN `new Duplexer().connect()` — a separate WebSocket AND a separate
 * `/api/auth/duplexer-instance` mint — for every mounted component that called a `use*Duplexer` hook. With
 * the home alone subscribing dashboard / file-system / assistant-chat (and each screen adding agent-task,
 * goal, workspace, knowledge…), a single view held a fistful of live sockets. This module mints the instance
 * once, opens ONE Duplexer + ONE VirtualSocket lazily on first use, and MULTIPLEXES every channel + listener
 * over it: a channel is `socket.subscribe`d once and fans its events out to all registered callbacks.
 *
 * HTTPS-only, same as before — duplexer-js picks ws:// on plain http and the browser blocks the upgrade
 * (local dev runs --experimental-https, so it works there too). The connection is kept alive for the page
 * lifetime; unsubscribing only drops a listener, never the shared socket, so navigation doesn't churn it.
 */

// The instance is minted lazily and re-minted on demand — duplexer-js asks for it on connect AND on
// token refresh, so a long-lived connection can re-authenticate instead of pinning one token forever
// (which is what the old per-hook version did). `InstanceError` carries the HTTP status because the
// gating below turns on it: see the note above `TRANSIENT_RETRY_MS`.
class InstanceError extends Error {
  constructor(
    message: string,
    /** HTTP status from /api/auth/duplexer-instance, or 0 when the request never completed. */
    readonly status: number,
  ) {
    super(message);
    this.name = 'InstanceError';
  }
}

async function fetchInstance(): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/auth/duplexer-instance`, { credentials: 'include' });
  } catch (err) {
    // A dropped/offline/aborted request — `TypeError: Failed to fetch` (`Load failed` on Safari).
    throw new InstanceError(err instanceof Error ? err.message : String(err), 0);
  }
  if (!res.ok) throw new InstanceError(`duplexer-instance ${res.status}`, res.status);
  const { instance } = (await res.json().catch(() => ({}))) as { instance?: string };
  if (!instance) throw new InstanceError('duplexer-instance missing', res.status);
  return instance;
}

/**
 * Why the instance is minted HERE and never handed to duplexer-js as a throwing callback.
 *
 * `instanceUpdater.getInstance` is called by duplexer-js from inside `connectWithBackoff`, once per
 * attempt, and that loop never gives up. Handing it a function that can REJECT turned every
 * signed-out tab — a visitor on the marketing landing, a session that expired overnight — into a
 * permanent unhandled-rejection generator: `duplexer-instance 401` and `Failed to fetch` were the
 * two loudest issues in Sentry (~20K events/30d across ORION-WEB-H/J/K/2/D/17), with duplexer's own
 * internals (`reading 'recoverable'`, `t.getStatus is not a function`) crashing behind them as each
 * doomed transport closed.
 *
 * So: mint FIRST, connect only with a token in hand, and give duplexer a getInstance that resolves
 * from cache. A caller with no identity now opens no socket at all instead of retrying forever.
 */
const TRANSIENT_RETRY_MS = 60_000;

interface ChannelRegistration {
  channel: { on(event: string, handler: (payload: DuplexerPayload<unknown>) => void): void };
  listeners: Map<DuplexerEventName, Set<Listener>>;
  bound: Set<DuplexerEventName>;
}

interface Socket {
  subscribe(channelName: string): ChannelRegistration['channel'];
}

let socketPromise: Promise<Socket> | null = null;
const channels = new Map<string, ChannelRegistration>();
/** The Duplexer behind `socketPromise`, kept only so a refusal can close it. */
let duplexerRef: { close(): void } | null = null;

let cachedInstance: string | null = null;
/** A caller who cannot be signed in for this page's lifetime — retrying cannot change the answer. */
let mintRefused = false;
let retryMintAfter = 0;
/** One mint at a time: duplexer asks per transport, and concurrent transports must not each fetch. */
let mintInFlight: Promise<string> | null = null;

/**
 * The OTHER way opening the connection fails: the mint succeeded and the setup after it did not —
 * a chunk that will not load, a constructor that throws, a `connect()` that rejects.
 *
 * It needs its own cooldown because `retryMintAfter` stays zero in that case, and a retry loop
 * governed only by the mint's cooldown would come straight back with no wait at all — an unbounded
 * request loop for as long as the subscribers stay mounted, which is the shape of the very bug this
 * module was rewritten to kill.
 */
let retryConnectAfter = 0;

/** When the next attempt to open the connection is allowed, whichever half failed last. */
function connectionNotBefore(): number {
  return Math.max(retryMintAfter, retryConnectAfter);
}

// 401 (no session) / 409 (no Orion metasite) are decisions about WHO the caller is, and a page that
// is not signed in does not become signed in without a navigation. Everything else — 5xx, a dropped
// request, a missing body — is a condition that can clear on its own, so it earns a cooldown.
function isRefusal(status: number): boolean {
  return status === 401 || status === 409;
}

/**
 * A refusal retires the connection, and the token with it.
 *
 * The minted instance is `signOrionInstance(secret, tenant.metaSiteId)` — a CREDENTIAL, scoped to
 * the tenant that was resolved when it was minted. So the one thing we must never do on a 401/409
 * is keep serving the old one: a revoked session or a tenant switch would otherwise reconnect the
 * shared socket under the PREVIOUS tenant's instance and stream its chat/task/file events into a
 * tab that is no longer that tenant's. Drop the token, close the socket, forget the channels, and
 * do not reopen — a caller who has to re-authenticate arrives back through a navigation anyway.
 */
function retireOnRefusal(): void {
  mintRefused = true;
  cachedInstance = null;
  // Deliberately does NOT clear the Sentry account id (lib/sentry-identity.ts). Dropping the
  // TOKEN here matters because serving it would stream another tenant's events; the id is only a
  // label, and clearing it is the one way to end up with a label that disagrees with the token the
  // app is actually using — instance-auth may still be serving a perfectly good one from its own
  // cache, and a cleared id would then be put back by it anyway. The id can only ever be
  // overwritten by a NEWER token, never resurrected from a stale one, so leaving it alone keeps
  // "whose session was this" readable without inventing a state that needs guarding.
  retryMintAfter = 0;
  retryConnectAfter = 0;
  const duplexer = duplexerRef;
  duplexerRef = null;
  socketPromise = null;
  channels.clear();
  try {
    duplexer?.close();
  } catch {
    // Already torn down by the transport — nothing left to close.
  }
}

async function runMint(): Promise<string> {
  try {
    const instance = await fetchInstance();
    cachedInstance = instance;
    retryMintAfter = 0;
    // The one moment the browser holds a tenant id without having asked for one. Sentry issues read
    // `0 users` without it, and volume is then the only signal they carry — see lib/sentry-identity.
    identifyFromInstance(instance);
    return instance;
  } catch (err) {
    const status = err instanceof InstanceError ? err.status : 0;
    if (isRefusal(status)) retireOnRefusal();
    else retryMintAfter = Date.now() + TRANSIENT_RETRY_MS;
    throw err;
  } finally {
    mintInFlight = null;
  }
}

function mintInstance(): Promise<string> {
  if (!mintInFlight) mintInFlight = runMint();
  return mintInFlight;
}

/**
 * What duplexer-js calls on connect and on every reconnect. Never rejects — a rejection here is what
 * the storm was made of — but it never hands back a REFUSED token either: `retireOnRefusal` has
 * already cleared the cache, so the fallback below has nothing to leak.
 *
 * Inside a transient cooldown it answers from cache rather than minting, because duplexer asks once
 * per backoff attempt and an endpoint that just failed does not want a request per retry.
 */
async function getInstanceForDuplexer(): Promise<string> {
  if (cachedInstance && retryMintAfter && Date.now() < retryMintAfter) return cachedInstance;
  try {
    return await mintInstance();
  } catch {
    return cachedInstance ?? '';
  }
}

async function ensureSocket(): Promise<Socket> {
  if (mintRefused) throw new InstanceError('duplexer unavailable for this caller', 401);
  const notBefore = connectionNotBefore();
  if (notBefore && Date.now() < notBefore) {
    throw new InstanceError('duplexer connection cooling down', 0);
  }
  if (!socketPromise) {
    socketPromise = (async () => {
      // Before the Duplexer exists, so a caller with no identity never starts a connect loop.
      await mintInstance();
      const { Duplexer } = await import('@wix/duplexer-js');
      const duplexer = new Duplexer(DUPLEXER_HOST, { instanceUpdater: { getInstance: getInstanceForDuplexer } });
      duplexerRef = duplexer;
      const socket = duplexer.connect({ appDefId: APP_DEF_ID }) as unknown as Socket;
      retryConnectAfter = 0;
      return socket;
    })().catch((err) => {
      // Let a later subscription retry the connect instead of caching the rejection forever — the
      // gates above are what keep that retry from being immediate. A mint failure has already set
      // its own cooldown; anything AFTER the mint (the chunk, the constructor, `connect()`) gets one
      // here, or the retry timer would fire straight back into the same failure.
      socketPromise = null;
      if (!mintRefused && !retryMintAfter) retryConnectAfter = Date.now() + TRANSIENT_RETRY_MS;
      throw err;
    });
  }
  return socketPromise;
}

/**
 * Subscribers that lost the race with a transient failure, waiting out the cooldown together.
 *
 * A `use*Duplexer` hook subscribes ONCE, in an effect that will not run again — so a subscriber whose
 * `ensureSocket` rejected has no second chance of its own, and a tab that happened to mount during a
 * blip would sit there with no live updates until the next navigation or reload. They retry here
 * instead, as a batch: `socketPromise` and `mintInFlight` collapse the whole batch into ONE connect
 * and ONE mint, so the cost is a single request per cooldown window however many are waiting.
 */
const pendingRetries = new Set<() => void>();
let retryTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRetry(attach: () => void): void {
  // A refusal is an answer, not a blip: nothing to wait for.
  if (mintRefused) return;
  pendingRetries.add(attach);
  if (retryTimer) return;
  retryTimer = setTimeout(
    () => {
      retryTimer = null;
      const due = [...pendingRetries];
      pendingRetries.clear();
      for (const run of due) run();
    },
    // Just past the cooldown `ensureSocket` enforces — whichever half of the connection failed — so
    // the first attempt through is allowed in rather than bounced straight back onto this timer.
    Math.max(connectionNotBefore() - Date.now(), 0) + 100,
  );
}

/**
 * Register a listener on a channel over the shared connection. Returns a synchronous disposer that removes
 * just this listener (the shared socket and the channel subscription stay up). Safe to dispose before the
 * async connect resolves.
 */
export function subscribeToChannel(
  channelName: string,
  events: DuplexerEventName[],
  listener: Listener,
): () => void {
  let disposed = false;
  let removeListener: (() => void) | null = null;

  const attach = (): void => {
    if (disposed || mintRefused) return;
    void ensureSocket()
      .then((socket) => {
        if (disposed) return;
        let reg = channels.get(channelName);
        if (!reg) {
          reg = { channel: socket.subscribe(channelName), listeners: new Map(), bound: new Set() };
          channels.set(channelName, reg);
        }
        const registration = reg;
        for (const event of events) {
          if (!registration.bound.has(event)) {
            registration.bound.add(event);
            registration.channel.on(event, (payload) => registration.listeners.get(event)?.forEach((fn) => fn(payload)));
          }
          let set = registration.listeners.get(event);
          if (!set) {
            set = new Set();
            registration.listeners.set(event, set);
          }
          set.add(listener);
        }
        removeListener = () => {
          for (const event of events) registration.listeners.get(event)?.delete(listener);
        };
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(`[orion-web/duplexer:${channelName}] setup failed`, err);
        scheduleRetry(attach);
      });
  };

  attach();

  return () => {
    disposed = true;
    removeListener?.();
  };
}

/**
 * How many distinct listeners this channel would fan a single event out to.
 *
 * One event runs `listeners.forEach(fn => fn(payload))`, so this number IS the multiplier on
 * whatever each listener does — and every `useTasks` instance registers one that refetches the
 * same cache entry. Read by the runaway-fetch probe so a burst report carries its own fan-out
 * factor instead of leaving it to be inferred from a stack.
 */
export function channelListenerCount(channelName: string): number {
  const registration = channels.get(channelName);
  if (!registration) return 0;
  const distinct = new Set<Listener>();
  registration.listeners.forEach((set) => set.forEach((listener) => distinct.add(listener)));
  return distinct.size;
}
